import { useEffect, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";

// 1080p re-encode of the cloud-texture loop (15s, ~2.5MB). The 4K master
// (bg-intro.mp4) stays in assets unimported — Vite won't bundle it — in case
// we later want a desktop variant.
import BgIntroVideo from "../assets/bg-intro-1080.mp4";

import "./IntroSequence.scss";

/**
 * Text-only intro sequence shown between the EnterSplash gate and the live
 * digital twin. Watercolor-sunset cloud background (pure CSS — no video) with
 * centered copy beats that fade in/out, paced to Sutu's presentation video
 * (Presentation_sutu_flock.mp4, ~60s). Ends by fading the whole overlay out
 * over the already-running flock behind it.
 *
 * All copy and timing live in BEATS / the timing constants below — edit there.
 * Styling: .intro-* in IntroSequence.scss (incl. the DejaVu Sans @font-face).
 */

interface IntroLine {
  text: ComponentChildren;
  /** Seconds after the beat's `inAt` that this line fades in. */
  delay: number;
}

interface IntroBeat {
  /** Seconds from intro start when the first line begins fading in. */
  inAt: number;
  /** Seconds from intro start when ALL lines of the beat fade out together. */
  outAt: number;
  lines: IntroLine[];
}

/** Default stagger between successive lines within a beat (seconds). */
const LINE_STAGGER = 1.8;

const line = (text: ComponentChildren, delay?: number): IntroLine => ({ text, delay: delay ?? NaN });

/** Resolve NaN delays to index * LINE_STAGGER so beats read as plain lists. */
const beat = (inAt: number, outAt: number, ...lines: IntroLine[]): IntroBeat => ({
  inAt,
  outAt,
  lines: lines.map((l, i) => (Number.isNaN(l.delay) ? { ...l, delay: i * LINE_STAGGER } : l)),
});

// Pacing matched to the presentation video: ~8s beats, lines staggered ~2s,
// long holds. The video's closing MURMURATIONS title card is intentionally
// not reproduced here — the hero wordmark over the flock plays that role.
const BEATS: IntroBeat[] = [
  beat(1, 8, line("Today, families are being torn apart"), line("by ongoing ICE raids across the United States.")),
  beat(9.5, 16, line("Local communities continue to fight"), line("to protect one another.")),
  beat(
    18,
    28,
    line(
      <>
        <strong>MURMURATIONS</strong> is a living archive
      </>,
    ),
    line("that carries these stories forward,"),
    line("inspired by the collective movement of starlings."),
  ),
  // Second line lands a long breath after the first, per the video.
  beat(30.5, 40.5, line("All of nature migrates."), line("Yet only human migration is criminalized.", 5)),
  beat(42.5, 50, line("Move toward the flock to listen."), line("And when you’re ready, add your voice.")),
];

/** Seconds from intro start when the overlay begins crossfading into the twin. */
const FADE_OUT_AT = 50.5;
/** Crossfade duration (seconds) — keep in sync with .intro.is-closing in the SCSS. */
const FADE_OUT_DURATION = 3;
/** Faster crossfade used when the user skips — sync with .intro.is-skipped. */
const SKIP_FADE_DURATION = 1.2;

interface IntroSequenceProps {
  /** Fires when the crossfade into the twin begins (natural end or skip) —
   * the shell ducks the music volume here. */
  onFadeBegin: () => void;
  /** Fires once the overlay is fully transparent; the parent unmounts it. */
  onComplete: () => void;
}

export function IntroSequence({ onFadeBegin, onComplete }: IntroSequenceProps) {
  // closing: natural-end crossfade. skipped: same, but faster.
  const [closing, setClosing] = useState(false);
  const [skipped, setSkipped] = useState(false);
  // Cloud video fades in over the CSS gradient once it's actually playing —
  // until then (slow connection, iOS low-power autoplay block) the gradient
  // sky underneath is the experience, so there's never a blank or a stall.
  const [videoPlaying, setVideoPlaying] = useState(false);
  const closingRef = useRef(false);
  const timersRef = useRef<number[]>([]);

  // Static gradient only for reduced-motion users — no drifting video.
  const [reduceMotion] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const beginFade = () => {
      closingRef.current = true;
      setClosing(true);
      onFadeBegin();
    };
    timersRef.current = [window.setTimeout(beginFade, FADE_OUT_AT * 1000), window.setTimeout(onComplete, (FADE_OUT_AT + FADE_OUT_DURATION) * 1000)];
    return () => timersRef.current.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hold the page still while the overlay is up — same deterministic
  // set/clear as EnterSplash. The gate is still fading out when we mount and
  // its unmount cleanup clears the lock ~450ms in, so re-assert it once the
  // gate is gone.
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const reassert = window.setTimeout(() => {
      document.body.style.overflow = "hidden";
    }, 600);
    return () => {
      window.clearTimeout(reassert);
      document.body.style.overflow = "";
    };
  }, []);

  const handleSkip = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    timersRef.current.forEach(clearTimeout);
    setSkipped(true);
    setClosing(true);
    onFadeBegin();
    timersRef.current = [window.setTimeout(onComplete, SKIP_FADE_DURATION * 1000)];
  };

  return (
    <div className={`intro${closing ? " is-closing" : ""}${skipped ? " is-skipped" : ""}`} role="dialog" aria-modal="true" aria-label="Introduction">
      {/* Drifting watercolor cloud layers — CSS fallback that owns the frame
          until the video is playing (and permanently if it never does). */}
      <div className="intro__sky" aria-hidden="true">
        <div className="intro__cloud intro__cloud--1" />
        <div className="intro__cloud intro__cloud--2" />
        <div className="intro__cloud intro__cloud--3" />
      </div>

      {/* Cloud-texture loop, fullscreen cover over the CSS sky. Keyed on the
          `playing` event (not canplay) so a blocked autoplay leaves us on the
          animated CSS clouds instead of a frozen first frame. */}
      {!reduceMotion && (
        <video
          className={`intro__video${videoPlaying ? " is-playing" : ""}`}
          src={BgIntroVideo}
          autoplay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          onPlaying={() => setVideoPlaying(true)}
        />
      )}

      {/* Copy beats — all stacked dead-centre; visibility is driven entirely
          by per-line animation delays. */}
      <div className="intro__copy">
        {BEATS.map((b, bi) => (
          <div className="intro__beat" key={bi}>
            {b.lines.map((l, li) => (
              <p key={li} className="intro__line" style={{ animationDelay: `${b.inAt + l.delay}s, ${b.outAt}s` }}>
                {l.text}
              </p>
            ))}
          </div>
        ))}
      </div>

      <button type="button" className="intro__skip" onClick={handleSkip}>
        Skip
      </button>
    </div>
  );
}
